/**
 * Geldfluss je Monat — Einnahmen nach oben, Kosten nach unten.
 *
 * <p>Das Nettoergebnis steht im Faktenband, nicht hier: beides in einem Diagramm wäre eine
 * Kurve zu viel.</p>
 */
import { useMemo } from 'react';
import { ChartDataTable } from '@/shared/charts/ChartDataTable';
import { EChart } from '@/shared/charts/EChart';
import { Card, CardBody, Eyebrow } from '@/shared/components';
import { formatMoney } from '@/shared/lib/format';
import type { PnLResponse } from '@/shared/api';
import { buildCashflowSeries, hasCashflow } from './cashflowSeries';
import { CHART_COLORS } from './financeChartTheme';

interface Props {
  pnl: PnLResponse;
}

const axisText = { color: CHART_COLORS.axis, fontSize: 11 };
const axisLine = { lineStyle: { color: CHART_COLORS.grid } };
const shortMoney = (value: number) =>
  Math.abs(value) >= 1000 ? `${Math.round(value / 1000)} T` : String(value);

export function CashflowChart({ pnl }: Props) {
  const data = useMemo(() => buildCashflowSeries(pnl), [pnl]);
  const hasData = hasCashflow(data);
  const money = (value: number) => formatMoney(Math.abs(value), pnl.currency);
  const option = useMemo(() => {
    const bar = (name: string, values: number[], color: string, stack: string) => ({
      name,
      type: 'bar',
      stack,
      data: values,
      itemStyle: { color, borderRadius: stack === 'pos' ? [3, 3, 0, 0] : [0, 0, 3, 3] },
    });
    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        backgroundColor: CHART_COLORS.tooltipBg,
        borderColor: CHART_COLORS.grid,
        textStyle: { fontSize: 12, color: CHART_COLORS.axis },
        valueFormatter: (value: number) => formatMoney(Math.abs(value), pnl.currency),
      },
      legend: { bottom: 0, textStyle: axisText },
      grid: { top: 8, right: 8, left: 44, bottom: 40 },
      xAxis: {
        type: 'category',
        data: data.map((row) => row.month),
        axisLabel: axisText,
        axisTick: { show: false },
        axisLine,
      },
      yAxis: {
        type: 'value',
        axisLabel: { ...axisText, formatter: shortMoney },
        axisLine: { show: false },
        splitLine: { lineStyle: { color: CHART_COLORS.grid, type: 'dashed' } },
      },
      series: [
        bar(
          'Einnahmen',
          data.map((row) => row.einnahmen),
          CHART_COLORS.income,
          'pos',
        ),
        bar(
          'Variable Kosten',
          data.map((row) => row.variableKosten),
          CHART_COLORS.variable,
          'neg',
        ),
        bar(
          'Laufende Verträge',
          data.map((row) => row.laufendeKosten),
          CHART_COLORS.recurring,
          'neg',
        ),
      ],
    };
  }, [data, pnl.currency]);

  return (
    <Card>
      <CardBody className="p-4 sm:p-5">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <Eyebrow variant="muted">Geldfluss je Monat</Eyebrow>
          <span className="text-ink-soft text-xs">
            Einnahmen nach oben, Kosten nach unten · {pnl.currency}
          </span>
        </div>

        {hasData ? (
          <>
            <EChart
              option={option}
              ariaLabel="Einnahmen und Kosten je Monat als Balken; die Werte stehen in der Tabelle darunter."
              className="mt-4 h-56 min-h-0 sm:h-64"
            />
            <ChartDataTable
              caption="Geldfluss je Monat"
              headers={['Monat', 'Einnahmen', 'Variable Kosten', 'Laufende Verträge']}
              rows={data.map((row) => ({
                key: row.month,
                label: row.month,
                values: [
                  money(row.einnahmen),
                  money(row.variableKosten),
                  money(row.laufendeKosten),
                ],
              }))}
            />
          </>
        ) : (
          <p className="text-ink-soft py-10 text-center text-sm">
            In diesem Zeitraum ist weder Geld eingegangen noch abgeflossen.
          </p>
        )}
      </CardBody>
    </Card>
  );
}
